import React, { memo, useMemo } from 'react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableExpandHeader,
  TableExpandRow,
  TableExpandedRow,
} from '@carbon/react';
import { array, bool, object, func } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Copy } from 'lucide-react';

import { StyledDataTableSkeleton } from 'Components/Order/OrderProducts';
import getAwbPath from 'Utils/getAwbPath';
import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';
import useToastNotifications from 'Hooks/useToastNotifications';

const StyledContainer = styled.div`
  width: 100%;
  display: grid;
  grid-gap: 2.25rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  .title {
    color: ${ props => props.theme.colors.darkGray };
    font-size: 1.875rem;
    font-weight: 600;
    margin-bottom: 10px;
  }

  table {
    border: 1px solid ${ props => props.theme.products.colors.border };

    thead {
      th {
        &:nth-of-type(2) {
          width: 35%;
        }
        background-color: ${ props => props.theme.products.colors.addToFavorite };
        font-size: 1rem;
        color: ${ props => props.theme.colors.darkGray };
        font-weight: 600;
      }
    }

    tbody {
      background-color: transparent;
      tr {
        &.cds--expandable-row[data-child-row="true"] td {
          background: transparent;
        }

        &.cds--parent-row.cds--expandable-row {
          + tr[data-child-row] td {
            padding: 0 16px;
          }

          td {
            background: #41618A;
            color: #fff;
            fill: #fff;

            a {
              color: #fff;
            }

            .cds--table-expand__svg {
              fill: #fff;
            }
          }
        }

        &:not(:last-child) {
          td {
            border-bottom: 1px solid ${ props => props.theme.products.colors.border };
          }
        }
      }

      td {
        background-color: transparent;
        font-size: 0.875rem;
        border: 0;

        .copy {
          margin-left: 8px;
          cursor: pointer;
        }
      }

      .awbProduct {
        display: flex;
        margin: 12px 0;
        color: inherit;

        .awbProductQuantity span {
          margin: 0 10px;
        }

        .awbProductName {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.25em;
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;

    .cds--data-table-content {
      overflow: hidden;
    }

    .cds--data-table >tbody tr.cds--parent-row {
      max-width: calc(100vw - 32px);

      + .cds--expandable-row {
        max-width: calc(100vw - 32px);
      }

      td {
        border: none;
      }
    }

    table {
      border: none;

      thead {
        display: none;
      }

      tbody {
        tr {
          position: relative;
          display: flex;
          flex-direction: column;
          height: auto;
          border: 1px solid #E2E2E2;

          :not(.cds--expandable-row) {
            margin-top: 20px;
          }

          :not(:last-child) td {
            border-bottom: none;
          }

          td.cds--table-expand{
            position: absolute;
            display: flex;
            justify-content: end;
            width: 100%;
            height: 45px;
            background: transparent !important;

            .cds--table-expand__button {
              width: 45px;
            }
          }
        }

        .awbProduct {
          .awbProductName {
            max-width: calc(100vw - 70px);
            -webkit-line-clamp: 1;
          }
        }

        td {
          padding: 12px;
          white-space: nowrap;

          &:before {
            content: attr(data-label);
            display: inline-block;
            min-width: 130px;
            font-weight: 600;
            text-transform: capitalize;
          }

          div {
            flex-wrap: wrap;
          }
        }
      }
    }
  }
`;

const OrderAWBs = memo(({
  awbs,
  awbsRef,
  loading,
  expandedRows,
  toggleExpandRow,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const toast = useToastNotifications();
  const analytics = useAnalytics();
  const headerData = useMemo(
    () => [
      {
        key: 'number',
        header: t('orders.awbColumns.number')
      },
      {
        key: 'courier',
        header: t('orders.awbColumns.courier')
      },
      {
        key: 'productsCount',
        header: t('orders.productsCount')
      },
      {
        key: 'link',
        header: ''
      }
    ],
    []
  );

  const oldHeaderData = useMemo(
    () => [
      {
        key: 'number',
        header: t('orders.awbColumns.number')
      },
      {
        key: 'courier',
        header: t('orders.awbColumns.courier')
      },
      {
        key: 'link',
        header: ''
      }
    ],
    []
  );

  const renderCell = cell => {
    if (cell.info.header === 'number') {
      return (
        <span className="awbNumber">
          { cell.value }
          <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(cell.value) } />
        </span>
      );
    }

    if (cell.info.header === 'link') {
      const { number, transporterName } = cell.value;

      return (
        <a
          href={ getAwbPath(number, transporterName) }
          onClick={ () => analytics.track('awb_link_clicked') }
          target="_blank"
        >
          { `${t('orders.seeAwbInfo')} >` }
        </a>
      );
    }

    return cell.value;
  };

  const renderExpandedRow = rowId => {
    const awb = awbs.find(item => item.id === rowId);
    if (!awb?.orderDetailsAwbs) {
      return null;
    }
    const products = awb.orderDetailsAwbs;

    return (
      <div>
        <ul>
          { products.map(({ product, quantity }) => (
            <li className="awbProduct" key={ product }>
              <div className="awbProductQuantity">{ quantity }<span>x</span></div>
              <div className="awbProductName">{ product }</div>
            </li>
          )) }
        </ul>
      </div>
    );
  };

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);
    analytics.track('awb_code_clipboard');

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <StyledContainer theme={ theme } id="orderAWBs" className="orderAwbs" ref={ awbsRef }>
      <div>
        <p className="title">{ t('orders.awbs') }</p>
        { loading && (
          <StyledDataTableSkeleton
            theme={ theme }
            showHeader={ false }
            showToolbar={ false }
            headers={ headers }
            columnCount={ headers.length }
            rowCount={ 5 }
          />
        ) }
        { (!loading && !awbs.length)
          ? t('orders.noAwbs')
          : (
            <DataTable rows={ awbs } headers={ awbs[0]?.productsCount ? headerData : oldHeaderData }>
              {({ rows, headers, getHeaderProps, getTableProps, getRowProps }) => (
                <TableContainer>
                  <Table { ...getTableProps() }>
                    <TableHead>
                      <TableRow>
                        { awbs[0]?.orderDetailsAwbs && <TableExpandHeader /> }
                        { headers.map(header => (
                          <TableHeader { ...getHeaderProps({ header }) }>
                            { header.header }
                          </TableHeader>
                        )) }
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      { rows.map(row => awbs[0]?.productsCount ? (
                        <React.Fragment key={ row.id }>
                          <TableExpandRow
                            { ...getRowProps({ row }) }
                            isExpanded={ expandedRows.has(row.id) }
                            onExpand={ () => toggleExpandRow(row.id) }
                          >
                            { row.cells.map(cell => (
                              <TableCell data-label={ cell.info.header } key={ cell.id }>{ renderCell(cell) }</TableCell>
                            )) }
                          </TableExpandRow>
                          <TableExpandedRow colSpan={ headers.length + 1 }>
                            { renderExpandedRow(row.id) }
                          </TableExpandedRow>
                        </React.Fragment>
                      ) : (
                        <TableRow { ...getRowProps({ row }) } key={ row.id }>
                          { row.cells.map(cell => (
                            <TableCell data-label={ cell.info.header } key={ cell.id }>{ renderCell(cell) }</TableCell>
                          )) }
                        </TableRow>
                      )) }
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DataTable>
          )
        }
      </div>
    </StyledContainer>
  );
});

OrderAWBs.propTypes = {
  awbs: array,
  awbsRef: object,
  loading: bool,
  expandedRows: object,
  toggleExpandRow: func,
};
OrderAWBs.displayName = 'OrderAWBsComponent';
export default OrderAWBs;
